import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCoA } from "../../context/CoAContext";
import { useCompany } from "../../context/CompanyContext";
import AddAccountForm from "./AddAccountForm";
import { supabase } from "../../Services/supabaseClient";
import "./account_chart.css";
import {
  Search,
  Filter,
  Layers,
  Trash2,
  Hash,
  ChevronDown,
  ChevronUp,
  Download,
  Pencil,
  Check,
  X,
} from "lucide-react";

const TYPE_COLORS = {
  Asset: "pill-asset",
  Liability: "pill-liability",
  Equity: "pill-equity",
  Revenue: "pill-revenue",
  Expense: "pill-expense",
};

export default function AccountChart() {
  const { accounts, deleteAccount } = useCoA();
  const { companyRole, companyId } = useCompany();

  // UI state
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [groupBy, setGroupBy] = useState("none"); // none | type | category
  const [expandedGroups, setExpandedGroups] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({
    account_number: "",
    name: "",
    type: "",
    category: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);

  // derive filters
  const types = useMemo(
    () => Array.from(new Set(accounts.map((a) => a.type))).filter(Boolean),
    [accounts]
  );
  const categories = useMemo(
    () => Array.from(new Set(accounts.map((a) => a.category))).filter(Boolean),
    [accounts]
  );

  // filtering + search
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return accounts.filter((a) => {
      const okType = typeFilter === "all" || a.type === typeFilter;
      const okCat = categoryFilter === "all" || a.category === categoryFilter;
      const okQ =
        !q ||
        String(a.account_number || "")
          .toLowerCase()
          .includes(q) ||
        String(a.name || "")
          .toLowerCase()
          .includes(q) ||
        String(a.type || "")
          .toLowerCase()
          .includes(q) ||
        String(a.category || "")
          .toLowerCase()
          .includes(q);
      return okType && okCat && okQ;
    });
  }, [accounts, query, typeFilter, categoryFilter]);

  // grouping
  const grouped = useMemo(() => {
    if (groupBy === "none") return { All: filtered };
    return filtered.reduce((acc, row) => {
      const key = (groupBy === "type" ? row.type : row.category) || "—";
      (acc[key] ||= []).push(row);
      return acc;
    }, {});
  }, [filtered, groupBy]);

  // toggle expand for groups
  const toggleGroup = (key) =>
    setExpandedGroups((p) => ({ ...p, [key]: !p[key] }));

  const handleDelete = (id) => {
    if (window.confirm("Delete this account?")) {
      deleteAccount(id);
    }
  };

  // start inline edit
  const beginEdit = (row) => {
    setEditingId(row.id);
    setDraft({
      account_number: row.account_number || "",
      name: row.name || "",
      type: row.type || "",
      category: row.category || "",
      description: row.description || "",
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setDraft({
      account_number: "",
      name: "",
      type: "",
      category: "",
      description: "",
    });
  };

  const saveEdit = async (id) => {
    if (!companyId) return;
    setSaving(true);
    try {
      // Update the presumed accounts table; scoped by company_id for safety.
      const { error } = await supabase
        .from("chart_of_accounts")
        .update({
          account_number: draft.account_number,
          name: draft.name,
          type: draft.type,
          category: draft.category,
          description: draft.description,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .eq("company_id", companyId);
      if (error) throw error;
      // Optimistic: close editing; useCoA context should rehydrate via its effect/poller.
      cancelEdit();
    } catch (err) {
      console.error("Error updating account:", err);
      alert("Failed to update account. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // CSV export (uses current filtered rows)
  const exportCSV = () => {
    const headers = [
      "Account Number",
      "Name",
      "Type",
      "Category",
      "Description",
    ];
    const rows = filtered.map((a) => [
      a.account_number ?? "",
      a.name ?? "",
      a.type ?? "",
      a.category ?? "",
      a.description ?? "",
    ]);
    const csv =
      [headers, ...rows]
        .map((r) =>
          r
            .map((cell) => {
              const c = String(cell ?? "");
              return `"${c.replace(/"/g, '""')}"`;
            })
            .join(",")
        )
        .join("\n") + "\n";

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chart-of-accounts_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const TypePill = ({ type }) => {
    const cls = TYPE_COLORS[type] || "pill";
    return <span className={`pill ${cls}`}>{type || "—"}</span>;
  };

  return (
    <motion.div
      className="coa-page"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      {/* Header + Add form */}
      <div className="coa-header">
        <div>
          <h2>Chart of Accounts</h2>
          <p className="coa-subtle">
            Manage your ledger structure. Search, filter, group, edit inline, or
            export CSV.
          </p>
        </div>
        <button className="ghost" onClick={exportCSV} title="Export CSV">
          <Download size={16} />
          <span>Export</span>
        </button>
      </div>

      {/* Keep create form intact */}
      <div className="coa-card">
        <AddAccountForm />
      </div>

      {/* Controls */}
      <div className="coa-controls coa-card">
        <div className="coa-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search by number, name, type, or category"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="coa-filters">
          <div className="filter-chip">
            <Filter size={14} />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              aria-label="Filter by Type"
            >
              <option value="all">All Types</option>
              {types.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-chip">
            <Filter size={14} />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              aria-label="Filter by Category"
            >
              <option value="all">All Categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-chip">
            <Layers size={14} />
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              aria-label="Group rows"
            >
              <option value="none">No Grouping</option>
              <option value="type">Group by Type</option>
              <option value="category">Group by Category</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table / Grouped lists */}
      {groupBy === "none" ? (
        <div className="coa-card">
          <div className="coa-table-wrap">
            <table className="coa-table">
              <thead>
                <tr>
                  <th className="narrow">
                    <Hash size={14} />
                  </th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Description</th>
                  {companyRole === "admin" && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {filtered.length > 0 ? (
                    filtered.map((acct) => {
                      const isEdit = editingId === acct.id;
                      return (
                        <motion.tr
                          key={acct.id}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                        >
                          <td className="mono">
                            {isEdit ? (
                              <input
                                className="inline-input mono"
                                value={draft.account_number}
                                onChange={(e) =>
                                  setDraft((d) => ({
                                    ...d,
                                    account_number: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              acct.account_number
                            )}
                          </td>
                          <td>
                            {isEdit ? (
                              <input
                                className="inline-input"
                                value={draft.name}
                                onChange={(e) =>
                                  setDraft((d) => ({
                                    ...d,
                                    name: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              <div className="name-cell">
                                <span className="name">{acct.name}</span>
                              </div>
                            )}
                          </td>
                          <td>
                            {isEdit ? (
                              <select
                                className="inline-select"
                                value={draft.type}
                                onChange={(e) =>
                                  setDraft((d) => ({
                                    ...d,
                                    type: e.target.value,
                                  }))
                                }
                              >
                                <option value="">—</option>
                                {[
                                  "Asset",
                                  "Liability",
                                  "Equity",
                                  "Revenue",
                                  "Expense",
                                ].map((t) => (
                                  <option key={t} value={t}>
                                    {t}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <TypePill type={acct.type} />
                            )}
                          </td>
                          <td>
                            {isEdit ? (
                              <input
                                className="inline-input"
                                value={draft.category}
                                onChange={(e) =>
                                  setDraft((d) => ({
                                    ...d,
                                    category: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              <span className="pill alt">
                                {acct.category || "—"}
                              </span>
                            )}
                          </td>
                          <td>
                            {isEdit ? (
                              <input
                                className="inline-input"
                                value={draft.description}
                                onChange={(e) =>
                                  setDraft((d) => ({
                                    ...d,
                                    description: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              <span className="meta">
                                {acct.description || ""}
                              </span>
                            )}
                          </td>

                          {companyRole === "admin" && (
                            <td className="action-col">
                              {isEdit ? (
                                <div className="row-actions">
                                  <button
                                    className="ghost success"
                                    disabled={saving}
                                    onClick={() => saveEdit(acct.id)}
                                    title="Save"
                                  >
                                    <Check size={16} />
                                  </button>
                                  <button
                                    className="ghost"
                                    disabled={saving}
                                    onClick={cancelEdit}
                                    title="Cancel"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <div className="row-actions">
                                  <button
                                    className="ghost"
                                    onClick={() => beginEdit(acct)}
                                    title="Edit"
                                  >
                                    <Pencil size={16} />
                                  </button>
                                  <button
                                    className="ghost danger"
                                    title="Delete account"
                                    onClick={() => handleDelete(acct.id)}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              )}
                            </td>
                          )}
                        </motion.tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={companyRole === "admin" ? 6 : 5}>
                        No accounts found.
                      </td>
                    </tr>
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        // Grouped layout
        <div className="coa-groups">
          {Object.entries(grouped).map(([key, rows]) => {
            const open = expandedGroups[key] ?? true;
            return (
              <div key={key} className="coa-card group-card">
                <button
                  className="group-header"
                  onClick={() => toggleGroup(key)}
                  aria-expanded={open}
                >
                  <div className="group-title">
                    {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    <strong>{key}</strong>
                    <span className="count">{rows.length} acct</span>
                  </div>
                </button>

                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="coa-table-wrap"
                    >
                      <table className="coa-table">
                        <thead>
                          <tr>
                            <th className="narrow">
                              <Hash size={14} />
                            </th>
                            <th>Name</th>
                            <th>Type</th>
                            <th>Category</th>
                            <th>Description</th>
                            {companyRole === "admin" && <th>Action</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((acct) => {
                            const isEdit = editingId === acct.id;
                            return (
                              <tr key={acct.id}>
                                <td className="mono">
                                  {isEdit ? (
                                    <input
                                      className="inline-input mono"
                                      value={draft.account_number}
                                      onChange={(e) =>
                                        setDraft((d) => ({
                                          ...d,
                                          account_number: e.target.value,
                                        }))
                                      }
                                    />
                                  ) : (
                                    acct.account_number
                                  )}
                                </td>
                                <td>
                                  {isEdit ? (
                                    <input
                                      className="inline-input"
                                      value={draft.name}
                                      onChange={(e) =>
                                        setDraft((d) => ({
                                          ...d,
                                          name: e.target.value,
                                        }))
                                      }
                                    />
                                  ) : (
                                    <div className="name-cell">
                                      <span className="name">{acct.name}</span>
                                    </div>
                                  )}
                                </td>
                                <td>
                                  {isEdit ? (
                                    <select
                                      className="inline-select"
                                      value={draft.type}
                                      onChange={(e) =>
                                        setDraft((d) => ({
                                          ...d,
                                          type: e.target.value,
                                        }))
                                      }
                                    >
                                      <option value="">—</option>
                                      {[
                                        "Asset",
                                        "Liability",
                                        "Equity",
                                        "Revenue",
                                        "Expense",
                                      ].map((t) => (
                                        <option key={t} value={t}>
                                          {t}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <TypePill type={acct.type} />
                                  )}
                                </td>
                                <td>
                                  {isEdit ? (
                                    <input
                                      className="inline-input"
                                      value={draft.category}
                                      onChange={(e) =>
                                        setDraft((d) => ({
                                          ...d,
                                          category: e.target.value,
                                        }))
                                      }
                                    />
                                  ) : (
                                    <span className="pill alt">
                                      {acct.category || "—"}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {isEdit ? (
                                    <input
                                      className="inline-input"
                                      value={draft.description}
                                      onChange={(e) =>
                                        setDraft((d) => ({
                                          ...d,
                                          description: e.target.value,
                                        }))
                                      }
                                    />
                                  ) : (
                                    <span className="meta">
                                      {acct.description || ""}
                                    </span>
                                  )}
                                </td>

                                {companyRole === "admin" && (
                                  <td className="action-col">
                                    {isEdit ? (
                                      <div className="row-actions">
                                        <button
                                          className="ghost success"
                                          disabled={saving}
                                          onClick={() => saveEdit(acct.id)}
                                          title="Save"
                                        >
                                          <Check size={16} />
                                        </button>
                                        <button
                                          className="ghost"
                                          disabled={saving}
                                          onClick={cancelEdit}
                                          title="Cancel"
                                        >
                                          <X size={16} />
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="row-actions">
                                        <button
                                          className="ghost"
                                          onClick={() => beginEdit(acct)}
                                          title="Edit"
                                        >
                                          <Pencil size={16} />
                                        </button>
                                        <button
                                          className="ghost danger"
                                          title="Delete account"
                                          onClick={() => handleDelete(acct.id)}
                                        >
                                          <Trash2 size={16} />
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
